import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DownloadOutlined } from '@ant-design/icons';
import {
  Button,
  Collapse,
  DatePicker,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  Table,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { ROUTES } from '@/constants';
import { cn } from '@/libs/utils';
import { downloadPayslipPdf } from '@/services/compensation.service';
import { updateEmployeeProfile } from '@/services/payrollEngine.service';

export const COMPENSATION_QUERY_KEY = 'employee-compensation';

const money = (value, digits = 2) =>
  value === null || value === undefined || value === ''
    ? '—'
    : Number(value).toLocaleString('sr-RS', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });

const date = (value) => (value ? dayjs(value).format('DD.MM.YYYY') : '—');

/**
 * The value the payroll profile stores for an amount shown in money: points are
 * the amount divided by an RSD point value, any other field is the amount.
 */
const toProfileValue = (salary, amount) =>
  salary.field === 'points' && salary.currency === 'RSD'
    ? Number(amount) / (salary.pointValue || 1)
    : Number(amount);

function SalaryCard({ salary, validFrom, tc }) {
  if (!salary) {
    return (
      <div className="rounded-xl border border-zinc-200 bg-white p-4 text-sm text-zinc-500">
        {tc('noSalary')}
      </div>
    );
  }
  const partTime = salary.workTimePercent && salary.workTimePercent < 100;
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4">
      <div className="text-[13px] text-zinc-500">{tc('contracted')}</div>
      <div className="mt-1 flex flex-wrap items-baseline gap-2">
        <span className="text-2xl font-extrabold">
          {money(
            partTime
              ? (salary.amount * salary.workTimePercent) / 100
              : salary.amount
          )}
        </span>
        <span className="text-sm font-semibold text-zinc-600">
          {salary.currency} · {tc(salary.kind === 'net' ? 'net' : 'gross')}
        </span>
      </div>
      <div className="mt-1 text-[13px] text-zinc-500">
        {validFrom ? tc('validFrom', { date: date(validFrom) }) : null}
        {partTime
          ? ` · ${tc('partTime', {
              percent: salary.workTimePercent,
              full: money(salary.amount),
            })}`
          : null}
      </div>
      <SalaryFigures breakdown={salary.breakdown} tc={tc} />
    </div>
  );
}

/** Net, gross 1 and gross 2 side by side, worked out by the engine. */
function SalaryFigures({ breakdown, tc }) {
  if (!breakdown) return null;
  const figures = [
    { key: 'net', label: tc('net'), value: breakdown.net },
    { key: 'gross1', label: tc('gross1'), value: breakdown.gross1 },
    { key: 'gross2', label: tc('gross2'), value: breakdown.gross2 },
  ];
  return (
    <div className="mt-3 border-t border-zinc-100 pt-3">
      <dl className="m-0 grid grid-cols-3 gap-3">
        {figures.map((f) => (
          <div key={f.key}>
            <dt className="text-[12px] text-zinc-500 first-letter:uppercase">
              {f.label}
            </dt>
            <dd className="m-0 font-semibold tabular-nums">{money(f.value)}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-2 text-[12px] text-zinc-400">{tc('breakdownNote')}</div>
    </div>
  );
}

/**
 * @param {Object} [props.profile] - Settings of a profile made by this save —
 *   the first salary of an employee who has none yet.
 */
function NewSalaryForm({ employeeId, salary, profile, onDone, tc }) {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const save = useMutation({
    mutationFn: ({ amount, validFrom, note }) =>
      updateEmployeeProfile(employeeId, {
        ...profile,
        [salary.field]: toProfileValue(salary, amount),
        salaryValidFrom: validFrom.startOf('day').format('YYYY-MM-DD'),
        salaryNote: note || '',
      }),
    onSuccess: () => {
      message.success(tc('saved'));
      queryClient.invalidateQueries({
        queryKey: [COMPENSATION_QUERY_KEY, employeeId],
      });
      queryClient.invalidateQueries({ queryKey: ['oz-employee-profiles'] });
      queryClient.invalidateQueries({ queryKey: ['company-compensation'] });
      onDone();
    },
    onError: (error) =>
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          tc('saveFailed')
      ),
  });

  return (
    <Form
      form={form}
      layout="vertical"
      className="grid grid-cols-2 gap-x-3 rounded-xl border border-zinc-200 bg-white p-4"
      initialValues={{ validFrom: dayjs().startOf('month') }}
      onFinish={(values) => save.mutate(values)}
    >
      <Form.Item
        name="amount"
        label={`${tc('newAmount')} (${salary.currency}, ${tc(
          salary.kind === 'net' ? 'net' : 'gross'
        )}${salary.workTimePercent < 100 ? `, ${tc('fullTime')}` : ''})`}
        rules={[{ required: true, message: tc('required') }]}
      >
        <InputNumber
          className="!w-full"
          min={0}
          precision={2}
          decimalSeparator=","
        />
      </Form.Item>
      <Form.Item
        name="validFrom"
        label={tc('validFromLabel')}
        rules={[{ required: true, message: tc('required') }]}
      >
        <DatePicker className="w-full" format="DD.MM.YYYY" />
      </Form.Item>
      <Form.Item name="note" label={tc('note')} className="col-span-2">
        <Input maxLength={300} />
      </Form.Item>
      <div className="col-span-2 flex justify-end gap-2">
        <Button shape="round" onClick={onDone}>
          {tc('cancel')}
        </Button>
        <Button
          shape="round"
          type="primary"
          htmlType="submit"
          loading={save.isPending}
        >
          {tc('save')}
        </Button>
      </div>
    </Form>
  );
}

function History({ history, tc }) {
  if (!history?.length) return null;
  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-4">
      <div className="mb-2 text-sm font-bold">{tc('history')}</div>
      <ul className="m-0 grid list-none gap-1.5 p-0">
        {history.map((h, i) => (
          <li
            key={`${h.validFrom}-${h.changedAt}-${i}`}
            className="flex flex-wrap items-baseline gap-x-2 border-b border-zinc-100 pb-1.5 text-sm last:border-none"
          >
            <span className="w-[92px] shrink-0 text-zinc-500">
              {date(h.validFrom)}
            </span>
            <span className="font-semibold">{money(h.value)}</span>
            {h.previous !== null && h.previous !== undefined ? (
              <span className="text-zinc-500">
                {tc('was', { amount: money(h.previous) })}
              </span>
            ) : null}
            {h.breakdown ? (
              <span className="text-zinc-500 tabular-nums">
                {`${tc('gross1')} ${money(h.breakdown.gross1)} · ${tc('gross2')} ${money(h.breakdown.gross2)}`}
              </span>
            ) : null}
            <span className="text-[12px] text-zinc-400">
              {[tc(`source_${h.source}`), h.changedBy, h.note]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Payslip({ payslip, tc }) {
  const isNet = payslip.format !== 'grossOnly';
  const columns = [
    { title: tc('code'), dataIndex: 'code', width: 70 },
    { title: tc('item'), dataIndex: 'name' },
    {
      title: tc('hours'),
      dataIndex: 'hours',
      align: 'right',
      width: 70,
      render: (v) => (v ? money(v, 0) : ''),
    },
    isNet && {
      title: <span className="inline-block first-letter:uppercase">{tc('net')}</span>,
      dataIndex: 'net',
      align: 'right',
      render: (v) => money(v),
    },
    {
      title: (
        <span className="inline-block first-letter:uppercase">{tc('gross')}</span>
      ),
      dataIndex: 'gross',
      align: 'right',
      render: (v) => money(v),
    },
  ].filter(Boolean);
  const rows = [
    ...(payslip.lines || []).map((l, i) => ({ key: `l${i}`, ...l })),
    payslip.seniority?.gross
      ? {
          key: 'seniority',
          code: '',
          name: tc('seniority', { rate: money(payslip.seniority.rate) }),
          net: payslip.seniority.net,
          gross: payslip.seniority.gross,
        }
      : null,
  ].filter(Boolean);
  const t = payslip.totals || {};
  // Read top to bottom like the payslip itself: gross, what is taken off it,
  // net, what is taken off that, the payout. The employer's side is apart.
  const statement = [
    { label: tc('gross'), value: t.gross, strong: true },
    { label: tc('tax'), value: t.tax, minus: true },
    { label: tc('employeeContributions'), value: t.employeeContributions, minus: true },
    isNet && { label: tc('net'), value: t.net, strong: true, line: true },
    t.deductions ? { label: tc('deductions'), value: t.deductions, minus: true } : null,
    t.advance ? { label: tc('advance'), value: t.advance, minus: true } : null,
    { label: tc('payout'), value: t.payout, strong: true, line: true, total: true },
  ].filter(Boolean);

  return (
    <div className="grid gap-4">
      <Table
        size="small"
        pagination={false}
        columns={columns}
        dataSource={rows}
        locale={{ emptyText: tc('noLines') }}
      />
      <div className="grid items-start gap-4 border-t border-zinc-200 pt-4 md:grid-cols-[minmax(0,1fr)_260px]">
        <dl className="m-0 grid max-w-[420px] text-sm">
          {statement.map((row) => (
            <div
              key={row.label}
              className={cn(
                'flex justify-between gap-4 px-2 py-1',
                row.line && 'mt-1 border-t border-zinc-300 pt-2',
                row.total && 'rounded-md bg-zinc-100 font-bold'
              )}
            >
              <dt
                className={cn(
                  'first-letter:uppercase',
                  row.strong ? 'font-semibold' : 'pl-3 text-zinc-500'
                )}
              >
                {row.minus ? '− ' : ''}
                {row.label}
              </dt>
              <dd
                className={cn(
                  'm-0 tabular-nums',
                  row.strong ? 'font-semibold' : 'text-zinc-600'
                )}
              >
                {money(row.value)}
              </dd>
            </div>
          ))}
        </dl>
        <div className="grid gap-1 rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-sm">
          <div className="mb-1 text-[12px] font-bold uppercase tracking-wide text-zinc-400">
            {tc('employerSide')}
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-zinc-500">{tc('employerContributions')}</span>
            <span className="tabular-nums">{money(t.employerContributions)}</span>
          </div>
          <div className="mt-1 flex justify-between gap-4 border-t border-zinc-200 pt-2 font-semibold">
            <span>{tc('totalCost')}</span>
            <span className="tabular-nums">
              {money((t.gross || 0) + (t.employerContributions || 0))}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

const PDF_LANGUAGES = ['sr', 'en', 'ru', 'zh'];

/** "PDF" with a language choice; the file is made on request, never stored. */
function PayslipDownload({ employeeId, period, tc }) {
  const [loading, setLoading] = useState(false);
  const download = async (language) => {
    setLoading(true);
    try {
      await downloadPayslipPdf(employeeId, period, language);
    } catch {
      message.error(tc('downloadFailed'));
    } finally {
      setLoading(false);
    }
  };
  return (
    // The header toggles the month; the button must not.
    <span onClick={(e) => e.stopPropagation()}>
      <Dropdown
        trigger={['click']}
        menu={{
          items: PDF_LANGUAGES.map((lang) => ({
            key: lang,
            label: tc(`pdf_${lang}`),
          })),
          onClick: ({ key }) => download(key),
        }}
      >
        <Button
          size="small"
          shape="round"
          icon={<DownloadOutlined />}
          loading={loading}
        >
          PDF
        </Button>
      </Dropdown>
    </span>
  );
}

function Payslips({ payslips, employeeId, tc, i18n }) {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-4">
      <div className="mb-2 text-sm font-bold">{tc('payslips')}</div>
      {!payslips?.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={tc('noPayslips')}
        />
      ) : (
        <Collapse
          size="small"
          items={payslips.map((p) => ({
            key: p.period,
            label: (
              <div className="flex flex-wrap justify-between gap-2">
                <span className="font-semibold capitalize">
                  {dayjs(new Date(p.year, p.month - 1, 1))
                    .locale(i18n.language)
                    .format('MMMM YYYY')}
                  {p.published === false && (
                    <Tag color="orange" className="ml-2 normal-case">
                      {tc('notPublished')}
                    </Tag>
                  )}
                </span>
                <span className="mr-3 text-zinc-500">
                  {p.format !== 'grossOnly'
                    ? `${tc('net')} ${money(p.totals?.net)} · `
                    : ''}
                  {tc('payout')} {money(p.totals?.payout)}
                </span>
              </div>
            ),
            extra: (
              <PayslipDownload
                employeeId={employeeId}
                period={p.period}
                tc={tc}
              />
            ),
            children: <Payslip payslip={p} tc={tc} />,
          }))}
        />
      )}
    </section>
  );
}

/**
 * The "Zarada" tile: the contracted salary as OZ holds it (one value, edited
 * here or in OZ alike), its history, and the monthly payslips from AB Soft.
 * Only HLB changes the salary; everyone who may open it sees it read-only.
 *
 * @param {Object} props
 * @param {Object} props.data - Response of GET /compensation/employees/:id.
 */
export default function CompensationPanel({ data }) {
  const { t, i18n } = useTranslation('translations');
  const tc = (key, opts) => t(`compensation.${key}`, opts);
  const [editing, setEditing] = useState(false);

  if (!data) return null;
  if (data.salaryHidden) {
    return <div className="py-6 text-center text-zinc-500">{tc('notAvailable')}</div>;
  }
  const { salary, firstSalary, history, payslips, access, employee } = data;
  const validFrom = history?.find((h) => h.field === salary?.field)?.validFrom;
  // A new employee has no salary yet: the first one is entered here too, with
  // the settings the company's other employees share (sent by the server).
  const formSalary = salary || firstSalary?.salary || null;
  const ozUrl = `${ROUTES.admin}/${ROUTES.oz}?company=${employee.company}&tab=profiles&employee=${employee._id}`;

  return (
    <div className="grid gap-3">
      <SalaryCard salary={salary} validFrom={validFrom} tc={tc} />

      {/* HLB only. "Open in payroll" is outlined in yellow like the other
          admin-only links (the "Payroll profile" button in the header). */}
      {access?.canEdit && (
        <div className="flex flex-wrap justify-end gap-2">
          {formSalary && !editing && (
            <Button shape="round" type="primary" onClick={() => setEditing(true)}>
              {tc(salary ? 'newSalary' : 'firstSalary')}
            </Button>
          )}
          <Link to={ozUrl}>
            <Button shape="round" className="!border-2 !border-[#FFCC00]">
              {tc('openOz')}
            </Button>
          </Link>
        </div>
      )}
      {editing && formSalary && (
        <NewSalaryForm
          employeeId={employee._id}
          salary={formSalary}
          profile={salary ? undefined : firstSalary?.profile}
          onDone={() => setEditing(false)}
          tc={tc}
        />
      )}

      <History history={history} tc={tc} />
      <Payslips
        payslips={payslips}
        employeeId={employee._id}
        tc={tc}
        i18n={i18n}
      />
    </div>
  );
}
